import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { authAPI } from '../lib/api'
import { Button, Card, Input, Logo } from '../components/ui'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await authAPI.requestPasswordReset(email)
      setSubmitted(true)
    } catch (err: any) {
      // Only transport/throttle failures land here — the endpoint answers
      // identically for known and unknown addresses by design.
      setError(err.message || 'Could not send the reset email. Try again in a moment.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/ISS_earth.avif)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

      <Card className="relative z-10 w-full max-w-md gap-6 p-6 shadow-2xl">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo className="mx-auto h-7 w-[231px] max-w-full" />
          <h1 className="text-base font-normal text-muted-foreground">Reset your password</h1>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              role="status"
              className="w-full rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm text-success"
            >
              If an account exists for that email address, a reset link is on its way. The link
              expires in one hour.
            </div>
            <Link
              to="/"
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Enter the email address on your account and we&rsquo;ll send you a link to set a new
              password.
            </p>

            <Input
              id="forgot-email"
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="h-11 w-full text-base"
              isLoading={loading}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>

            <Link
              to="/"
              className="text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Back to login
            </Link>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground">
          DoW Acquisitions &amp; Vendor Evaluation Platform
        </p>
      </Card>
    </div>
  )
}
